import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useDomain } from '../contexts/DomainContext';
import { useAuth } from '../contexts/AuthContext';
import AIVisibilityScore from '../components/GEO/AIVisibilityScore';
import GEOStatCard from '../components/GEO/GEOStatCard';
import GEOTimeline from '../components/GEO/GEOTimeline';
import AIPromptVisibility from '../components/GEO/AIPromptVisibility';
import AICitationShare from '../components/GEO/AICitationShare';
import AIAnswerSnapshot from '../components/GEO/AIAnswerSnapshot';
import AICitationHistory from '../components/GEO/AICitationHistory';
import {
  ClipboardDocumentListIcon,
  DocumentMagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  PlayIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { runGeoScan, getGeoUsage, getGeoEngines } from '../services/geoService';

// Breadcrumb component
const GEOBreadcrumb = ({ items }) => {
  return (
    <nav aria-label="GEO breadcrumb navigation" className="mb-4">
      <ol className="flex items-center space-x-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
        {items.map((item, i) => (
          <li key={i} className="flex items-center" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            {i > 0 && <span className="mx-2 text-gray-400">/</span>}
            {item.href ? (
              <a href={item.href} className="text-indigo-600 hover:text-indigo-800" itemProp="item">
                <span itemProp="name">{item.label}</span>
              </a>
            ) : (
              <span className="text-gray-600 font-medium" itemProp="name">{item.label}</span>
            )}
            <meta itemProp="position" content={String(i + 1)} />
          </li>
        ))}
      </ol>
    </nav>
  );
};

// Empty state for initial load
const EMPTY_DASHBOARD = {
  visibilityScore: 0,
  scoreTrend: [],
  aiMentions: 0,
  aiMentionsChange: 0,
  aiCitations: 0,
  aiCitationsChange: 0,
  trackedPrompts: 0,
  trackedPromptsChange: 0,
  competitorMentions: 0,
  competitorMentionsChange: 0,
  timeline: [],
  prompts: [],
  citationShare: [],
  snapshots: [],
  citationHistory: []
};

// Transform API scan results to dashboard component format
function transformScanResults(scanData, domain) {
  const pr = scanData.promptResults || [];
  const dash = scanData.dashboard || {};

  // Build prompts array for AIPromptVisibility
  const prompts = pr.map((item, idx) => {
    const engines = item.engines || [];
    const gEng = engines.find(e => e.engine === 'google_ai') || {};
    const pEng = engines.find(e => e.engine === 'perplexity') || {};
    const cEng = engines.find(e => e.engine === 'chatgpt') || {};
    const coEng = engines.find(e => e.engine === 'copilot') || {};
    // Find first citation source
    let citationName = null;
    let citationColor = 'text-gray-500';
    for (const eng of engines) {
      if (eng.cited && eng.sources && eng.sources.length > 0) {
        const src = eng.sources[0];
        try { citationName = typeof src === 'string' ? new URL(src).hostname.replace('www.','') : (src.name || String(src)); } catch(e) { citationName = typeof src === 'string' ? src.split('/')[2] || src : String(src || 'Unknown'); }
        break;
      }
    }
    return {
      id: idx + 1,
      prompt: item.prompt,
      chatgpt: cEng.mentioned || false,
      perplexity: pEng.mentioned || false,
      googleAI: gEng.mentioned || false,
      copilot: coEng.mentioned || false,
      mentions: item.mentions || 0,
      citation: citationName ? { name: citationName, color: citationColor } : null
    };
  });

  // Build citation share for AICitationShare
  const engineNames = { google_ai: 'Google AI', perplexity: 'Perplexity', chatgpt: 'ChatGPT', copilot: 'Copilot' };
  const engineStats = {};
  pr.forEach(item => {
    (item.engines || []).forEach(eng => {
      if (!engineStats[eng.engine]) engineStats[eng.engine] = { mentions: 0, citations: 0, total: 0 };
      engineStats[eng.engine].total++;
      if (eng.mentioned) engineStats[eng.engine].mentions++;
      if (eng.cited) engineStats[eng.engine].citations++;
    });
  });
  const citationShare = Object.entries(engineStats).map(([key, s]) => ({
    engine: engineNames[key] || key,
    promptCoverage: s.total > 0 ? Math.round((s.mentions / s.total) * 100) : 0,
    citationFrequency: s.citations,
    share: s.total > 0 ? Math.round((s.citations / Math.max(s.total, 1)) * 100) : 0
  }));

  // Build snapshots for AIAnswerSnapshot
  const snapshots = pr.map((item, idx) => {
    const engines = item.engines || [];
    const gEng = engines.find(e => e.engine === 'google_ai') || {};
    const pEng = engines.find(e => e.engine === 'perplexity') || {};
    const cEng = engines.find(e => e.engine === 'chatgpt') || {};
    const coEng = engines.find(e => e.engine === 'copilot') || {};
    let topSource = null;
    for (const eng of engines) {
      if (eng.rank && eng.rank > 0) {
        try { topSource = eng.sources && eng.sources[0] ? (typeof eng.sources[0] === 'string' ? new URL(eng.sources[0]).hostname.replace('www.','') : String(eng.sources[0].name || eng.sources[0])) : null; } catch(e) { topSource = eng.sources && eng.sources[0] ? String(eng.sources[0]) : null; }
        break;
      }
    }
    return {
      id: idx + 1,
      prompt: item.prompt,
      chatgpt: cEng.mentioned || false,
      perplexity: pEng.mentioned || false,
      googleAI: gEng.mentioned || false,
      copilot: coEng.mentioned || false,
      rank: { change: null, position: item.avgRank > 0 ? item.avgRank : null, source: topSource }
    };
  });

  // Build citation history matching AICitationHistory component format
  const citationHistory = pr.slice(0, 3).map((item, idx) => {
    const citations = [];
    (item.engines || []).forEach(eng => {
      const engName = engineNames[eng.engine] || eng.engine;
      if (eng.sources && eng.sources.length > 0) {
        eng.sources.slice(0, 2).forEach(src => {
          const srcUrl = typeof src === 'string' ? src : (src.url || '');
          let domainStr = '';
          try { domainStr = srcUrl ? new URL(srcUrl).hostname.replace('www.','') : ''; } catch(e) { domainStr = srcUrl; }
          citations.push({
            source: engName,
            domain: domainStr || engName.toLowerCase(),
            rank: eng.cited ? String(eng.rank || '') : null
          });
        });
      } else {
        citations.push({
          source: engName,
          domain: eng.engine + '.ai',
          rank: null
        });
      }
    });
    return {
      prompt: item.prompt.length > 30 ? item.prompt.substring(0, 30) + '...' : item.prompt,
      rankBadge: { rank: idx + 1, label: idx === 0 ? 'Latest' : '' },
      citations: citations.slice(0, 5)
    };
  });

  // Build timeline (single scan = single point)
  const now = new Date();
  const timeline = [{ date: now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), mentions: dash.totalMentions || 0 }];

  return {
    visibilityScore: dash.visibilityScore || 0,
    scoreTrend: [{ month: now.toLocaleDateString('en-US', { month: 'short' }), score: dash.visibilityScore || 0 }],
    aiMentions: dash.totalMentions || 0,
    aiMentionsChange: 0,
    aiCitations: dash.totalCitations || 0,
    aiCitationsChange: 0,
    trackedPrompts: dash.promptCount || 0,
    trackedPromptsChange: 0,
    competitorMentions: 0,
    competitorMentionsChange: 0,
    timeline,
    prompts,
    citationShare,
    snapshots,
    citationHistory
  };
}

const GEODashboard = () => {
  const { selectedDomain } = useDomain();
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState('30');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [dashboardData, setDashboardData] = useState(EMPTY_DASHBOARD);
  const [hasScanResults, setHasScanResults] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [engines, setEngines] = useState([]);
  const [selectedEngines, setSelectedEngines] = useState(['google_ai', 'perplexity', 'chatgpt']);
  const [promptInputs, setPromptInputs] = useState(['']);
  const [scanDomain, setScanDomain] = useState('');

  // Load available engines and usage on mount
  useEffect(() => {
    const loadMeta = async () => {
      try {
        const engData = await getGeoEngines();
        if (engData && engData.engines) setEngines(engData.engines);
      } catch (e) { console.error('Failed to load engines:', e); }
      try {
        const usageData = await getGeoUsage();
        if (usageData) setUsage(usageData);
      } catch (e) { console.error('Failed to load usage:', e); }
    };
    loadMeta();
  }, []);

  // Set domain from context
  useEffect(() => {
    if (selectedDomain && !scanDomain) {
      setScanDomain(selectedDomain);
    }
  }, [selectedDomain, scanDomain]);

  const addPromptInput = () => {
    if (promptInputs.length < 10) {
      setPromptInputs([...promptInputs, '']);
    }
  };

  const removePromptInput = (idx) => {
    setPromptInputs(promptInputs.filter((_, i) => i !== idx));
  };

  const updatePromptInput = (idx, val) => {
    const updated = [...promptInputs];
    updated[idx] = val;
    setPromptInputs(updated);
  };

  const toggleEngine = (engineId) => {
    setSelectedEngines(prev =>
      prev.includes(engineId) ? prev.filter(e => e !== engineId) : [...prev, engineId]
    );
  };

  const handleScan = async () => {
    const validPrompts = promptInputs.filter(p => p.trim().length > 0);
    if (validPrompts.length === 0) {
      setScanError('Please enter at least one prompt to scan.');
      return;
    }
    if (selectedEngines.length === 0) {
      setScanError('Please select at least one AI engine.');
      return;
    }
    const domain = scanDomain.trim() || selectedDomain || '';
    if (!domain) {
      setScanError('Please enter a domain to scan.');
      return;
    }

    setScanning(true);
    setScanError(null);
    try {
      const result = await runGeoScan(domain, validPrompts, selectedEngines);
      if (result && result.data) {
        const transformed = transformScanResults(result.data, domain);
        setDashboardData(transformed);
        setHasScanResults(true);
      } else {
        setScanError('Scan completed but no data was returned.');
      }
      // Refresh usage
      try {
        const usageData = await getGeoUsage();
        if (usageData) setUsage(usageData);
      } catch (e) { /* ignore */ }
    } catch (err) {
      console.error('GEO scan failed:', err);
      setScanError(err.message || 'Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'GEO', href: '/geo' },
    { label: 'GEO Dashboard' }
  ];

  return (
    <>
      <Helmet>
        <title>GEO Dashboard | AI Visibility Monitoring | SERPfinity™</title>
        <meta name="description" content="Monitor and optimize your brand presence across generative AI search engines like ChatGPT, Perplexity, Google AI Overview, and Copilot. Track AI mentions, citations, and prompt coverage with SERPfinity™." />
        <meta name="keywords" content="GEO dashboard, generative engine optimization, AI visibility score, AI mentions tracker, AI citations monitoring, ChatGPT brand tracking, Perplexity monitoring, Google AI Overview tracking, Copilot mentions, prompt visibility, AI search optimization, SERPfinity™" />
        <link rel="canonical" href="https://thedigitalhall.club/geo" />
        <meta property="og:title" content="GEO Dashboard | AI Visibility Monitoring | SERPfinity™" />
        <meta property="og:description" content="Monitor and optimize your brand presence across generative AI search engines. Track AI mentions, citations, prompt visibility, and competitive positioning with SERPfinity™." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thedigitalhall.club/geo" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GEO Dashboard | AI Visibility Monitoring | SERPfinity™" />
        <meta name="twitter:description" content="Track your brand visibility across AI search engines. Monitor ChatGPT, Perplexity, Google AI Overview, and Copilot citations and mentions." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "GEO Dashboard",
            "description": "Monitor brand visibility across generative AI search engines with SERPfinity™.",
            "url": "https://thedigitalhall.club/geo",
            "isPartOf": { "@type": "WebApplication", "name": "SERPfinity™" }
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <GEOBreadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">GEO Dashboard</h1>
            <p className="mt-1 text-gray-600">Monitor brand visibility across generative AI search engines like ChatGPT, Perplexity, Google AI Overview, and Copilot.</p>
          </header>

          {/* Scan Panel */}
          <section aria-label="GEO scan configuration" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <DocumentMagnifyingGlassIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                Run AI Visibility Scan
              </h2>
              {usage && (
                <div className="text-sm text-gray-500" aria-label="API usage status">
                  <span className="font-medium">{usage.monthlyApiCalls || 0}</span> API calls this month
                </div>
              )}
            </div>

            {/* Domain input */}
            <div className="mb-4">
              <label htmlFor="geo-scan-domain" className="block text-sm font-medium text-gray-700 mb-1">Domain to analyze</label>
              <input
                id="geo-scan-domain"
                type="text"
                value={scanDomain}
                onChange={(e) => setScanDomain(e.target.value)}
                placeholder="e.g. serpfinity.ai"
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                aria-describedby="domain-help"
              />
              <p id="domain-help" className="mt-1 text-xs text-gray-500">Enter the domain you want to check across AI engines</p>
            </div>

            {/* Prompt inputs */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Prompts to scan</label>
              <div className="space-y-2">
                {promptInputs.map((val, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => updatePromptInput(idx, e.target.value)}
                      placeholder={`e.g. best AI SEO tools`}
                      className="flex-1 max-w-lg px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      aria-label={`Prompt ${idx + 1}`}
                    />
                    {promptInputs.length > 1 && (
                      <button
                        onClick={() => removePromptInput(idx)}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                        aria-label={`Remove prompt ${idx + 1}`}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {promptInputs.length < 10 && (
                <button
                  onClick={addPromptInput}
                  className="mt-2 inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                  aria-label="Add another prompt"
                >
                  <PlusIcon className="h-4 w-4" /> Add prompt
                </button>
              )}
            </div>

            {/* Engine selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">AI Engines</label>
              <div className="flex flex-wrap gap-3" role="group" aria-label="Select AI engines to scan">
                {engines.filter(e => e.status === 'active').map(eng => (
                  <button
                    key={eng.id}
                    onClick={() => toggleEngine(eng.id)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      selectedEngines.includes(eng.id)
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700 font-medium'
                        : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                    }`}
                    aria-pressed={selectedEngines.includes(eng.id)}
                    aria-label={`${eng.name} engine`}
                  >
                    {eng.name}
                  </button>
                ))}
                {engines.filter(e => e.status === 'coming_soon').map(eng => (
                  <span key={eng.id} className="px-3 py-1.5 rounded-full text-sm border border-dashed border-gray-300 text-gray-400 cursor-default" aria-label={`${eng.name} coming soon`}>
                    {eng.name} (soon)
                  </span>
                ))}
              </div>
            </div>

            {/* Error message */}
            {scanError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" role="alert">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-red-700">{scanError}</p>
              </div>
            )}

            {/* Scan button */}
            <button
              onClick={handleScan}
              disabled={scanning}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium transition-colors ${
                scanning ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
              }`}
              aria-busy={scanning}
            >
              {scanning ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Scanning AI engines...
                </>
              ) : (
                <>
                  <PlayIcon className="h-4 w-4" aria-hidden="true" />
                  Run Scan
                </>
              )}
            </button>
          </section>

          {/* Empty state when no scan has been run */}
          {!hasScanResults && !scanning && (
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center mb-6" aria-label="No scan results">
              <DocumentMagnifyingGlassIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" aria-hidden="true" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">No Scan Results Yet</h2>
              <p className="text-gray-500 max-w-md mx-auto">Enter your prompts and domain above, then click &quot;Run Scan&quot; to see how your brand appears across AI search engines.</p>
            </section>
          )}

          {/* Dashboard results - show after scan or during loading */}
          {(hasScanResults || scanning) && (
            <>
              {/* KPI Cards Row */}
              <section aria-label="AI visibility key metrics" className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="lg:col-span-1">
                    <AIVisibilityScore
                      score={dashboardData.visibilityScore}
                      trend={dashboardData.scoreTrend}
                      loading={scanning}
                    />
                  </div>
                  <GEOStatCard
                    title="AI Mentions"
                    value={dashboardData.aiMentions}
                    change={dashboardData.aiMentionsChange}
                    icon={ChatBubbleLeftRightIcon}
                    loading={scanning}
                  />
                  <GEOStatCard
                    title="AI Citations"
                    value={dashboardData.aiCitations}
                    change={dashboardData.aiCitationsChange}
                    icon={ClipboardDocumentListIcon}
                    loading={scanning}
                  />
                  <GEOStatCard
                    title="Tracked Prompts"
                    value={dashboardData.trackedPrompts}
                    change={dashboardData.trackedPromptsChange}
                    icon={DocumentMagnifyingGlassIcon}
                    loading={scanning}
                  />
                  <GEOStatCard
                    title="Competitor Mentions"
                    value={dashboardData.competitorMentions}
                    change={dashboardData.competitorMentionsChange}
                    icon={UserGroupIcon}
                    loading={scanning}
                  />
                </div>
              </section>

              {/* Timeline */}
              <section aria-label="AI mentions timeline" className="mb-6">
                <GEOTimeline data={dashboardData.timeline} loading={scanning} />
              </section>

              {/* Middle Row: Prompt Visibility + Citation Share */}
              <section aria-label="AI prompt visibility and citation analysis" className="mb-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <AIPromptVisibility data={dashboardData.prompts} loading={scanning} />
                  </div>
                  <div>
                    <AICitationShare data={dashboardData.citationShare} loading={scanning} />
                  </div>
                </div>
              </section>

              {/* Bottom Row: Answer Snapshot + Citation History */}
              <section aria-label="AI answer snapshots and citation history" className="mb-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <AIAnswerSnapshot data={dashboardData.snapshots} loading={scanning} />
                  </div>
                  <div>
                    <AICitationHistory data={dashboardData.citationHistory} loading={scanning} />
                  </div>
                </div>
              </section>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default GEODashboard;
